import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiFetch } from "@/utils/api";

export type UserRole = "admin" | "sales" | "viewer";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
}

interface AuthContextValue {
  token: string | null;
  user: AuthUser | null;
  loading: boolean;
  setSession: (token: string, user: AuthUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem("authToken"));
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const raw = localStorage.getItem("authUser");
      return raw ? (JSON.parse(raw) as AuthUser) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    apiFetch<{ user: AuthUser }>("/auth/me", { token })
      .then((d) => {
        setUser(d.user);
        localStorage.setItem("authUser", JSON.stringify(d.user));
      })
      .catch(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem("authToken");
        localStorage.removeItem("authUser");
      })
      .finally(() => setLoading(false));
  }, [token]);

  const setSession = (t: string, u: AuthUser) => {
    setToken(t);
    setUser(u);
    localStorage.setItem("authToken", t);
    localStorage.setItem("authUser", JSON.stringify(u));
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem("authToken");
    localStorage.removeItem("authUser");
  };

  return (
    <AuthContext.Provider value={{ token, user, loading, setSession, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function canWrite(user: AuthUser | null): boolean {
  return user?.role === "admin" || user?.role === "sales";
}

export function isAdmin(user: AuthUser | null): boolean {
  return user?.role === "admin";
}
